import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertDocumentInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertDocumentInternalAuditResultInput = {
  InternalAuditReportId: string;
  DocumentIds: string[];
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertDocumentInternalAuditResultMutation => ({
  insertChildDocumentInternalAuditResult: {
    __typename: 'InsertChildDocumentAssessmentResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertDocumentInternalAuditResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.insertDocument.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertDocumentInternalAuditResult: async (
      variables: InsertDocumentInternalAuditResultInput
    ): Promise<InsertDocumentInternalAuditResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
